import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box } from "@mui/material";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";

import { GridColDef } from "@mui/x-data-grid-premium";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import { renderNumberCell } from "utils/dataGrid";

interface BrandMetadataReviewProps {
  chain_id: string;
  country_code: string;
}

const columns_def: GridColDef[] = [
  {
    field: "chain_id",
    headerName: "Chain ID / Brand ID",
    type: "string",
    width: 200,
  },
  {
    field: "metadata_brand_name",
    headerName: "Metadata Name",
    description:
      "Name associated with the brand from a list of all names in brand metadata",
    type: "string",
    width: 250,
  },
  {
    field: "actual_place_name",
    headerName: "Actual Place Name",
    description: "A place name associated with the brand in the places table.",
    type: "string",
    width: 250,
  },
  {
    field: "place_name_count",
    headerName: "Place Name Count",
    description:
      "The count of places associated with this brand name in this country.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
];

const ReviewBrandNamesByCountry: React.FC<BrandMetadataReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  useEffect(() => {
    if (chain_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
        country_code: country_code,
      });

      const fetchBrandNames = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/business_names_by_country?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch business name to review for ${chain_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandNames();
    } else {
      setData([]);
    }
  }, [chain_id, country_code]);

  const user_instructions = {
    section_id: "brand-country-names",
    instruction_title: "Review Names",
    purpose:
      "Accurate metadata about the names a brand is known by is crucial for ensuring high-quality results in our chain identification process. This process associates places in our database with specific brands by assigning a unique `chain_id` value to each brand. The `chain_id` remains consistent across all places associated with the brand, ensuring accurate grouping and identification.\n\nIn addition to enhancing the accuracy of the chain identification process, maintaining a comprehensive list of valid names serves as a reference during the places review process. Typically, invalid names are linked to invalid places that should not be associated with the brand.",
    goal: "Ensure all names for the brand, associated with the selected country of operation, are accurately reflected in the metadata.",
    task_description:
      "The table below compares the names listed in the `names` attribute in the metadata with the actual name values linked to places in the dataplor database for the brand.",
    task_steps: [
      {
        title: "Identify Missing Names",
        instructions: [
          "Review the table below to identify any names associated with places in the database that are missing from the metadata.",
        ],
      },
      {
        title: "Validate Missing Names",
        instructions: [
          "Research and verify whether the missing names are applicable to the brand for the selected country of operation.",
        ],
      },
      {
        title: "Update Metadata",
        instructions: [
          {
            text: "For validated names, update the `names` attribute in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Brand Edit`.",
              "Update the metadata to include the missing names.",
            ],
          },
        ],
      },
    ],
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      <GaneshDataGrid
        gridId="reviewBrandNamesByCountry"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewBrandNamesByCountry;
